import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Search } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { toast } from 'react-hot-toast';

const AddHomeContent = () => {
  const navigate = useNavigate();
  const { contentId } = useParams();
  const isEditMode = !!contentId;

  const {
    uploadImage,
    getHomeContentMeta,
    getSubscriptions,
    getHomeContentById,
    addCardDirectly,
    updateCardDirectly
  } = useFirebase();

  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    testCounts: 0,
    createdDate: new Date().toISOString().split('T')[0],
    status: 'published',
    plan: '',
    discountPlan: '',
    index: '',
    collectionName: ''
  });

  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFree, setIsFree] = useState(true);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [selectedPkgId, setSelectedPkgId] = useState(null);

  // Fetch next index and load collections on component mount
  useEffect(() => {
    if (isEditMode) {
      loadCard();
    } else {
      fetchNextIndex();
    }
    loadCollections();
  }, [contentId]);

  const loadCard = async () => {
    try {
      setLoading(true);
      const card = await getHomeContentById(contentId);
      if (card) {
        setFormData({
          title: card.title || card.CollectionName || '',
          imageUrl: card.imageUrl || '',
          testCounts: card.testCounts || 0,
          createdDate: new Date().toISOString().split('T')[0],
          status: card.status || 'published',
          plan: card.plan || '',
          discountPlan: card.discountPlan || '',
          index: card.index || '',
          collectionName: card.CollectionName || card.title || ''
        });
        setIsFree(!card.plan || card.plan === 'Free');
      }
    } catch (error) {
      console.error('Error loading card:', error);
      toast.error('Failed to load card');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextIndex = async () => {
    try {
      const meta = await getHomeContentMeta();
      const nextIndex = (meta?.lastIndex || 0) + 1;
      setFormData(prev => ({ ...prev, index: nextIndex.toString() }));
    } catch (error) {
      console.warn('Could not fetch next index:', error);
      setFormData(prev => ({ ...prev, index: '1' }));
    }
  };

  const loadCollections = async () => {
    try {
      const subscriptions = await getSubscriptions();
      const allCollections = subscriptions.map(s => ({
        ...s,
        id: s.id,
        title: s.CollectionName || s.collectionName,
        imageUrl: s.collectionImageUrl || s.imageUrl || '',
        amount: s.SubscriptionAmount || s.amount || 'Free',
        discountPlan: s.discountPlan || ''
      }));
      setAvailableCollections(allCollections);
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const handleCollectionSelect = (collection) => {
    setFormData(prev => ({
      ...prev,
      collectionName: collection.title,
      imageUrl: collection.imageUrl,
      plan: collection.amount === 'Free' ? '' : collection.amount,
      discountPlan: collection.discountPlan
    }));
    setIsFree(collection.amount === 'Free');
    setSearchTerm(collection.title);
    setSelectedPkgId(collection.id); // Store the subscription ID
    setShowCollectionDropdown(false);
    toast.success(`Selected: ${collection.title}`);
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCollectionDropdown && !event.target.closest('.collection-select-container')) {
        setShowCollectionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCollectionDropdown]);

  const filteredCollections = availableCollections.filter(collection =>
    collection.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));

    if (imageFile) {
      handleFileUpload(imageFile);
    }
  };

  const handleFileUpload = async (file) => {
    if (file && file.type.startsWith('image/')) {
      try {
        setLoading(true);

        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({
          ...prev,
          imageUrl: previewUrl
        }));

        const fileName = `home-content/${Date.now()}-${file.name}`;
        const downloadUrl = await uploadImage(file, fileName);

        setFormData(prev => ({
          ...prev,
          imageUrl: downloadUrl
        }));

        URL.revokeObjectURL(previewUrl);

      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image. Please try again.');
        setFormData(prev => ({
          ...prev,
          imageUrl: ''
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setLoading(true);

      const cardData = {
        title: formData.title || "",
        imageUrl: formData.imageUrl || "",
        testCounts: parseInt(formData.testCounts) || 0,
        plan: isFree ? null : (formData.plan || ""),
        discountPlan: formData.discountPlan || "",
        index: formData.index || undefined,
      };

      if (isEditMode) {
        await updateCardDirectly(contentId, cardData);
        toast.success('Card updated successfully!');
        navigate('/home');
      } else {
        // Pass selectedPkgId to link the card to the subscription
        const newCardId = await addCardDirectly(cardData, selectedPkgId);
        toast.success('Card added successfully!');
        navigate('/home');
      }
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error('Error saving card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/home')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold">
          {isEditMode ? 'Edit Card' : 'Add Card'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Card Details</h2>

          {/* Optional Collection Name Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Collection Name (Optional)
            </label>
            <div className="relative collection-select-container">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowCollectionDropdown(true);
                  }}
                  onFocus={() => setShowCollectionDropdown(true)}
                  onClick={() => setShowCollectionDropdown(true)}
                  className="input pl-10 w-full"
                  placeholder="Search for a subscription collection to auto-fill..."
                />
              </div>

              {showCollectionDropdown && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {availableCollections.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 italic">
                      No subscription collections found.
                    </div>
                  ) : filteredCollections.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 italic">
                      No matching collections found.
                    </div>
                  ) : (
                    filteredCollections.map((collection) => (
                      <button
                        key={collection.id}
                        type="button"
                        onClick={() => handleCollectionSelect(collection)}
                        className="w-full px-4 py-3 text-left hover:bg-indigo-50 flex items-center space-x-3 border-b border-gray-100 last:border-0 transition-colors"
                      >
                        {collection.imageUrl && (
                          <div className="w-12 h-12 rounded border border-gray-200 overflow-hidden bg-gray-100 flex-shrink-0">
                            <img
                              src={collection.imageUrl}
                              alt={collection.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{collection.title}</p>
                          <p className="text-xs text-gray-500">
                            {collection.amount}
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Optional: Select a subscription collection to auto-fill image, plan, and discount plan
            </p>
          </div>

          {/* Upload Image Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Image *
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${loading
                ? 'border-gray-200 bg-gray-50'
                : dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                }`}
              onDragOver={loading ? undefined : handleDragOver}
              onDragLeave={loading ? undefined : handleDragLeave}
              onDrop={loading ? undefined : handleDrop}
            >
              <div className="mx-auto w-12 h-12 text-gray-400 mb-4">
                <Upload className="w-full h-full" />
              </div>
              <div className="text-gray-600">
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Uploading image...</span>
                  </div>
                ) : (
                  <>
                    <label className="cursor-pointer text-blue-600 hover:text-blue-700">
                      Click to upload
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                        disabled={loading}
                      />
                    </label>
                    {' '}or drag and drop
                  </>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                PNG, JPG, GIF up to 10MB
              </p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Index No. *
              </label>
              <input
                type="number"
                name="index"
                value={formData.index}
                readOnly
                className="input w-full bg-gray-100 cursor-not-allowed"
                placeholder="Auto-generated"
              />
              <p className="text-xs text-gray-500 mt-1">This determines the display order of the card.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Enter card title..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tests Count
              </label>
              <input
                type="number"
                name="testCounts"
                value={formData.testCounts}
                onChange={handleInputChange}
                className="input w-full"
                min="0"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan
              </label>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="isFree"
                  checked={isFree}
                  onChange={(e) => setIsFree(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isFree" className="ml-2 text-sm text-gray-700">
                  Free
                </label>
              </div>
              {!isFree && (
                <input
                  type="text"
                  name="plan"
                  value={formData.plan}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="Enter plan amount (e.g., 50 rs, 100 rs)..."
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Plan
              </label>
              <input
                type="text"
                name="discountPlan"
                value={formData.discountPlan}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Enter discount plan (e.g., 10% off, Festive offer)..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <input
                type="date"
                name="createdDate"
                value={formData.createdDate}
                onChange={handleInputChange}
                className="input w-full"
              />
            </div>
          </div>

          {/* Image Preview */}
          {formData.imageUrl && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Preview
              </label>
              <div className="relative inline-block">
                <img
                  src={formData.imageUrl}
                  alt="Preview"
                  className="w-32 h-20 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, imageUrl: '' }));
                  }}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Card' : 'Save Card'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddHomeContent;